import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const CustomLabeling = () => {
  const { getAccountId } = useAuth();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [labelingInProgress, setLabelingInProgress] = useState({});
  const [labeledAssets, setLabeledAssets] = useState([]);
  const [selectedLabelKey, setSelectedLabelKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Form state for creating new label
  const [formData, setFormData] = useState({
    labelKey: '',
    labelType: 'binary',
    categoricalValues: '',
    description: ''
  });

  useEffect(() => {
    fetchDefinitions();
  }, []);

  useEffect(() => {
    if (selectedLabelKey) {
      fetchLabeledAssets(selectedLabelKey);
    }
  }, [selectedLabelKey, currentPage]);

  const fetchDefinitions = async () => {
    try {
      setLoading(true);
      const accountId = getAccountId();
      const response = await fetch('/api/custom-labels/definitions', {
        headers: {
          'x-account-id': accountId
        }
      });

      const data = await response.json();
      if (data.success) {
        setDefinitions(data.definitions);
      } else {
        toast.error('Failed to fetch label definitions');
      }
    } catch (error) {
      console.error('Error fetching definitions:', error);
      toast.error('Error fetching label definitions');
    } finally {
      setLoading(false);
    }
  };

  const fetchLabeledAssets = async (labelKey, page = 1) => {
    try {
      const accountId = getAccountId();
      const response = await fetch(`/api/custom-labels/assets?labelKey=${labelKey}&page=${page}&limit=20`, {
        headers: {
          'x-account-id': accountId
        }
      });

      const data = await response.json();
      if (data.success) {
        setLabeledAssets(data.assets);
        setTotalPages(data.totalPages);
      } else {
        toast.error('Failed to fetch labeled assets');
      }
    } catch (error) {
      console.error('Error fetching labeled assets:', error);
      toast.error('Error fetching labeled assets');
    }
  };

  const handleCreateLabel = async (e) => {
    e.preventDefault();
    
    try {
      const accountId = getAccountId();
      
      // Parse categorical values if type is categorical
      let categoricalValues = null;
      if (formData.labelType === 'categorical') {
        categoricalValues = formData.categoricalValues
          .split(',')
          .map(v => v.trim())
          .filter(v => v.length > 0);
        
        if (categoricalValues.length === 0) {
          toast.error('Please provide at least one categorical value');
          return;
        }
      }

      const response = await fetch('/api/custom-labels/definitions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-account-id': accountId
        },
        body: JSON.stringify({
          labelKey: formData.labelKey,
          labelType: formData.labelType,
          categoricalValues,
          description: formData.description
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Label definition created successfully!');
        setShowCreateModal(false);
        setFormData({
          labelKey: '',
          labelType: 'binary',
          categoricalValues: '',
          description: ''
        });
        fetchDefinitions();
      } else {
        toast.error(data.error || 'Failed to create label definition');
      }
    } catch (error) {
      console.error('Error creating label:', error);
      toast.error('Error creating label definition');
    }
  };

  const handleLabelAssets = async (definitionId, labelKey) => {
    try {
      setLabelingInProgress(prev => ({ ...prev, [definitionId]: true }));
      const accountId = getAccountId();

      const response = await fetch('/api/custom-labels/label-assets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-account-id': accountId
        },
        body: JSON.stringify({
          labelDefinitionId: definitionId
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Successfully labeled ${data.labeled} assets!`);
        if (selectedLabelKey === labelKey) {
          fetchLabeledAssets(labelKey, currentPage);
        }
      } else {
        toast.error(data.error || 'Failed to label assets');
      }
    } catch (error) {
      console.error('Error labeling assets:', error);
      toast.error('Error labeling assets');
    } finally {
      setLabelingInProgress(prev => ({ ...prev, [definitionId]: false }));
    }
  };

  const handleDeleteDefinition = async (id, labelKey) => {
    if (!window.confirm(`Are you sure you want to delete the label "${labelKey}"? This will also delete all associated label values.`)) {
      return;
    }

    try {
      const accountId = getAccountId();
      const response = await fetch(`/api/custom-labels/definitions/${id}`, {
        method: 'DELETE',
        headers: {
          'x-account-id': accountId
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Label definition deleted successfully');
        fetchDefinitions();
        if (selectedLabelKey === labelKey) {
          setSelectedLabelKey(null);
          setLabeledAssets([]);
        }
      } else {
        toast.error(data.error || 'Failed to delete label definition');
      }
    } catch (error) {
      console.error('Error deleting definition:', error);
      toast.error('Error deleting label definition');
    }
  };

  const handleUpdateLabelValue = async (assetId, labelKey, newValue) => {
    try {
      const accountId = getAccountId();
      const response = await fetch(`/api/custom-labels/assets/${assetId}/${labelKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-account-id': accountId
        },
        body: JSON.stringify({
          labelValue: newValue
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Label updated successfully');
        fetchLabeledAssets(selectedLabelKey, currentPage);
      } else {
        toast.error(data.error || 'Failed to update label');
      }
    } catch (error) {
      console.error('Error updating label:', error);
      toast.error('Error updating label');
    }
  };

  const renderLabelValue = (asset, definition) => {
    const currentValue = asset.label_value;
    
    if (definition.label_type === 'binary') {
      return (
        <select
          value={currentValue || ''}
          onChange={(e) => handleUpdateLabelValue(asset.asset_id, definition.label_key, e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">Not set</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      );
    } else if (definition.label_type === 'categorical') {
      const options = definition.categorical_values || [];
      return (
        <select
          value={currentValue || ''}
          onChange={(e) => handleUpdateLabelValue(asset.asset_id, definition.label_key, e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">Not set</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    } else if (definition.label_type === 'number') {
      return (
        <input
          type="number"
          step="any"
          value={currentValue || ''}
          onChange={(e) => handleUpdateLabelValue(asset.asset_id, definition.label_key, e.target.value)}
          onBlur={(e) => {
            if (e.target.value) {
              handleUpdateLabelValue(asset.asset_id, definition.label_key, e.target.value);
            }
          }}
          className="border rounded px-2 py-1 text-sm w-24"
        />
      );
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Labeling</h1>
          <p className="text-gray-600">Create custom brand labels and automatically label your assets using AI</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create New Label
        </button>
      </div>

      {/* Label Definitions List */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Label Definitions</h2>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        ) : definitions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No label definitions yet. Create your first label to get started!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {definitions.map(def => (
              <div
                key={def.id}
                className={`border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedLabelKey === def.label_key ? 'border-blue-500 bg-blue-50' : ''
                }`}
                onClick={() => setSelectedLabelKey(def.label_key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-900">{def.label_key}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        def.label_type === 'binary' ? 'bg-green-100 text-green-800' :
                        def.label_type === 'categorical' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {def.label_type}
                      </span>
                    </div>
                    {def.description && (
                      <p className="text-sm text-gray-600 mt-1">{def.description}</p>
                    )}
                    {def.label_type === 'categorical' && def.categorical_values && (
                      <p className="text-xs text-gray-500 mt-1">
                        Values: {def.categorical_values.join(', ')}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Created: {new Date(def.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLabelAssets(def.id, def.label_key);
                      }}
                      disabled={labelingInProgress[def.id]}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {labelingInProgress[def.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          Labeling...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Label Assets
                        </>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDefinition(def.id, def.label_key);
                      }}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Labeled Assets Table */}
      {selectedLabelKey && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              Labeled Assets: {selectedLabelKey}
            </h2>
            <button
              onClick={() => setSelectedLabelKey(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {labeledAssets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No assets labeled yet. Click "Label Assets" to start AI labeling.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ad Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Media Format</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Label Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {labeledAssets.map(asset => {
                      const definition = definitions.find(d => d.label_key === selectedLabelKey);
                      return (
                        <tr key={`${asset.asset_id}-${asset.label_key}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{asset.ad_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{asset.media_format}</td>
                          <td className="px-4 py-3 text-sm">
                            {definition && renderLabelValue(asset, definition)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <span className={`px-2 py-1 rounded text-xs ${
                              asset.is_ai_generated ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {asset.is_ai_generated ? 'AI' : 'Manual'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(asset.updated_at).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Create Label Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Create New Label</h2>
            <form onSubmit={handleCreateLabel}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label Key (unique identifier)
                </label>
                <input
                  type="text"
                  value={formData.labelKey}
                  onChange={(e) => setFormData({ ...formData, labelKey: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g., brand_tier, product_category"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label Type
                </label>
                <select
                  value={formData.labelType}
                  onChange={(e) => setFormData({ ...formData, labelType: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="binary">Binary (Yes/No)</option>
                  <option value="categorical">Categorical (Multiple values)</option>
                  <option value="number">Number (Float)</option>
                </select>
              </div>

              {formData.labelType === 'categorical' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categorical Values (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.categoricalValues}
                    onChange={(e) => setFormData({ ...formData, categoricalValues: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g., Premium, Standard, Basic"
                    required={formData.labelType === 'categorical'}
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows="3"
                  placeholder="Describe what this label represents..."
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({
                      labelKey: '',
                      labelType: 'binary',
                      categoricalValues: '',
                      description: ''
                    });
                  }}
                  className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Label
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomLabeling;

